<template>
  <teleport to="body">
    <div v-if="open && boundary" class="core-tier-backdrop" @click.self="$emit('close')">
      <div
        class="core-modal-surface bg-white p-6 w-[520px] max-w-full border border-neutral-300"
        role="dialog"
        aria-modal="true"
        :aria-label="texts.modalTitle">
        <div class="mb-3 flex items-start justify-between gap-4">
          <h2 class="text-xl font-semibold text-neutral-900">{{ texts.modalTitle }}</h2>
          <button
            type="button"
            class="text-neutral-550 hover:text-neutral-900"
            :aria-label="t('general.button.close')"
            @click="$emit('close')">
            <IconX class="h-5 w-5" stroke="1.8" />
          </button>
        </div>
        <p class="mb-5 text-sm leading-snug text-neutral-550">{{ texts.modalText }}</p>

        <!-- Choosing a value applies it right away; the X or a click outside cancels. -->
        <div class="space-y-2">
          <button
            v-for="option in options"
            :key="option.choice"
            type="button"
            class="core-tier-option"
            :class="{ 'is-active': decision?.choice === option.choice }"
            :aria-pressed="decision?.choice === option.choice"
            :data-testid="`tier-boundary-option-${option.choice}`"
            @click="$emit('confirm', option.choice)">
            <span class="text-sm text-neutral-700">{{ option.label }}</span>
            <span class="whitespace-nowrap font-medium tabular-nums text-neutral-900">
              {{ option.minutes }} {{ t('general.min_per_sq_m') }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { IconX } from '@tabler/icons-vue'
import type { ProjectCalculationTierBoundary, UsageCorrection } from '@arcexperts/core-types'

/** VAL-102: lets the user choose between the values of the two intervals next to a boundary. */
export type TierDecision = NonNullable<UsageCorrection['tierDecision']>
export type TierBoundaryTexts = Record<'modalTitle' | 'modalText' | 'optionCurrent' | 'optionAdjacent', string>

const props = defineProps<{
  open: boolean
  boundary: ProjectCalculationTierBoundary | null
  decision: TierDecision | null
  texts: TierBoundaryTexts
}>()
defineEmits<{
  (e: 'confirm', choice: TierDecision['choice']): void
  (e: 'close'): void
}>()

const { t } = useI18n()
const options = computed(() =>
  props.boundary
    ? [
        {
          choice: 'current' as const,
          label: props.texts.optionCurrent,
          minutes: Math.round(props.boundary.currentMinutes),
        },
        {
          choice: 'adjacent' as const,
          label: props.texts.optionAdjacent,
          minutes: Math.round(props.boundary.adjacentMinutes),
        },
      ]
    : []
)
</script>

<style lang="postcss" scoped>
.core-tier-backdrop {
  @apply fixed inset-0 overflow-y-auto overflow-x-hidden;
  @apply z-70 flex items-center justify-center;
  @apply bg-neutral-100/15 backdrop-blur-[2px];
}

.core-modal-surface {
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.12),
    0 8px 16px -12px rgba(0, 0, 0, 0.2);
}

.core-tier-option {
  @apply flex w-full cursor-pointer items-center justify-between gap-4 border border-neutral-300 bg-white px-4 py-3 text-left;

  &:hover {
    background-color: #f5f5f5;
  }

  &.is-active {
    border-color: color-mix(in srgb, var(--color-primary-default) 48%, white);
    background-color: color-mix(in srgb, var(--color-primary-default) 3%, white);
  }

  &:focus-visible {
    @apply outline outline-2 outline-offset-2;
    outline-color: var(--color-primary-default);
  }
}
</style>
